import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Trophy, AlertCircle, HelpCircle, Bot, User } from "lucide-react";
import GameOptions from "./GameOptions";
import PlayerNameInput from "./PlayerNameInput";
import { useGameBot } from "./useGameBot";

/**
 * Ultimate Tic-Tac-Toe
 */
const UltimateTicTacToeGame = () => {
  const navigate = useNavigate();

  // --- Constants ---
  const PLAYER_X = "X";
  const PLAYER_O = "O";

  // --- State ---
  const [gameStarted, setGameStarted] = useState(false);
  const [gameMode, setGameMode] = useState("PvC"); // PvC or PvP
  const [difficulty, setDifficulty] = useState("Easy");
  const [player1Name, setPlayer1Name] = useState("Player 1");
  const [player2Name, setPlayer2Name] = useState("Player 2");

  // globalBoard: 3x3 array capturing the state of each local board.
  // Values: null (playing), PLAYER_X (X won), PLAYER_O (O won), 'DRAW' (full w/o winner)
  const [globalBoard, setGlobalBoard] = useState(Array(9).fill(null));

  const [localBoards, setLocalBoards] = useState(
    Array(9)
      .fill(null)
      .map(() => Array(9).fill(null))
  );

  const [currentPlayer, setCurrentPlayer] = useState(PLAYER_X);
  const [nextBoardIndex, setNextBoardIndex] = useState(null); // null means any board is valid
  const [winner, setWinner] = useState(null); // 'X', 'O', 'DRAW'
  const [showRules, setShowRules] = useState(false);

  // --- Helpers ---
  const checkWinner = (squares) => {
    const lines = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8],
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8],
      [0, 4, 8],
      [2, 4, 6],
    ];

    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return null;
  };

  const isBoardFull = (squares) => {
    return squares.every((square) => square !== null);
  };

  const getPlayerName = (playerSymbol) => {
    if (playerSymbol === PLAYER_X) return player1Name;
    if (gameMode === "PvC" && playerSymbol === PLAYER_O) return "Computer";
    return player2Name;
  };

  // --- Actions ---

  const handleMove = useCallback((boardIndex, squareIndex) => {
     // This needs to return a function that modifies state, or modifies state directly?
     // useGameBot expects us to execute the move.
     // But for "simulation" in AI, we need a pure version.
     // Let's separate the logic.
     return;
  }, []);

  // --- Logic for State Updates (Pure-ish) ---
  const simulateMove = (currentLocalBoards, currentGlobalBoard, currentNextBoard, player, bIdx, sIdx) => {
      // 1. Update Local Board
      const newLocalBoards = currentLocalBoards.map(board => [...board]);
      newLocalBoards[bIdx][sIdx] = player;

      // 2. Update Global Board Status if needed
      const newGlobalBoard = [...currentGlobalBoard];
      const localWinner = checkWinner(newLocalBoards[bIdx]);
      if (localWinner) {
          newGlobalBoard[bIdx] = localWinner;
      } else if (isBoardFull(newLocalBoards[bIdx])) {
          newGlobalBoard[bIdx] = "DRAW";
      }

      // 3. Check Global Win
      const gameWinner = checkWinner(newGlobalBoard);
      let gameResult = null;
      if (gameWinner) gameResult = gameWinner;
      else if (isBoardFull(newGlobalBoard)) gameResult = "DRAW";

      // 4. Determine Next Board
      let nextBoard = null;
      if (!gameResult && newGlobalBoard[sIdx] === null) {
          nextBoard = sIdx;
      } else {
          nextBoard = null; // Free move
      }

      return {
          localBoards: newLocalBoards,
          globalBoard: newGlobalBoard,
          nextBoardIndex: nextBoard,
          winner: gameResult,
          nextPlayer: player === PLAYER_X ? PLAYER_O : PLAYER_X
      };
  };

  const executeMove = (boardIndex, squareIndex) => {
      if (winner) return;
      if (nextBoardIndex !== null && nextBoardIndex !== boardIndex) return;
      if (globalBoard[boardIndex] !== null) return;
      if (localBoards[boardIndex][squareIndex] !== null) return;

      const result = simulateMove(localBoards, globalBoard, nextBoardIndex, currentPlayer, boardIndex, squareIndex);

      setLocalBoards(result.localBoards);
      setGlobalBoard(result.globalBoard);
      setNextBoardIndex(result.nextBoardIndex);
      setWinner(result.winner);
      setCurrentPlayer(result.nextPlayer);
  };

  const startGame = () => {
    setGlobalBoard(Array(9).fill(null));
    setLocalBoards(Array(9).fill(null).map(() => Array(9).fill(null)));
    setCurrentPlayer(PLAYER_X);
    setNextBoardIndex(null);
    setWinner(null);
    setGameStarted(true);
  };

  const resetGame = () => {
      setGameStarted(false);
  };

  // --- AI Logic ---

  const getValidMoves = (lBoards, gBoard, nBoardIdx) => {
      const moves = [];
      // If nBoardIdx is null, can play in any board that isn't finished (null in gBoard)
      // If nBoardIdx is set, can only play in that board (if it's not finished, but logic ensures it's not)
      
      const targetBoards = nBoardIdx !== null ? [nBoardIdx] : [0,1,2,3,4,5,6,7,8].filter(i => gBoard[i] === null);

      for (let bIdx of targetBoards) {
          for (let sIdx = 0; sIdx < 9; sIdx++) {
              if (lBoards[bIdx][sIdx] === null) {
                  moves.push({ b: bIdx, s: sIdx });
              }
          }
      }
      return moves;
  };

  const evaluateState = (gBoard, lBoards, player) => {
      // Heuristic evaluation
      // Win = 1000
      // Loss = -1000
      // Local Win = 50
      // Local Center = 2
      // Local Corner = 1
      const opponent = player === PLAYER_X ? PLAYER_O : PLAYER_X;
      
      const gWinner = checkWinner(gBoard);
      if (gWinner === player) return 10000;
      if (gWinner === opponent) return -10000;

      let score = 0;
      
      // Evaluate Global Board status
      for (let i = 0; i < 9; i++) {
          if (gBoard[i] === player) score += 50;
          else if (gBoard[i] === opponent) score -= 50;
      }

      // Evaluate Local position strength (lightly)
      // ... (could be expensive to iterate all 81 squares)

      return score;
  };

  const getBotMove = useCallback((diff, gameState) => {
      const { localBoards, globalBoard, nextBoardIndex, currentPlayer } = gameState;
      const validMoves = getValidMoves(localBoards, globalBoard, nextBoardIndex);
      
      if (validMoves.length === 0) return null;

      // Easy: Random
      if (diff === "Easy") {
          return validMoves[Math.floor(Math.random() * validMoves.length)];
      }

      // Medium: Win local, Block local, Avoid sending to full board (simple)
      // Hard: 1-ply simulation + Heuristics
      
      // Converting valid moves to a format we can score
      let bestMove = null;
      let bestScore = -Infinity;
      
      // Determine "Self" for AI (is it X or O?)
      // The function is called 'getBotMove', and it's AI's turn (currentPlayer).
      const aiPlayer = currentPlayer; 
      const humanPlayer = aiPlayer === PLAYER_X ? PLAYER_O : PLAYER_X;

      // Shuffle moves to add randomness to equal choices
      const shuffledMoves = validMoves.sort(() => 0.5 - Math.random());

      for (let move of shuffledMoves) {
          let score = 0;
          
          // Simulation
          const sim = simulateMove(localBoards, globalBoard, nextBoardIndex, aiPlayer, move.b, move.s);
          
          // 1. Check Global Win (Immediate)
          if (sim.winner === aiPlayer) return move; // Always take global win
          
          // 2. Check Local Win (Medium+)
          if (sim.globalBoard[move.b] === aiPlayer && globalBoard[move.b] === null) {
              score += 100;
          }

          // 3. Check Block Local Win (simulate if opponent played there)
          // (Requires checking if opponent IS about to win in this cell)
          // Let's do a quick check: if we assume the board was same but it was Opponent's turn?
          // Actually, simply: Check if this move completes a line for current player (Attack), 
          // or if we DON'T take it, could opponent take it?
          
          // Let's explicitly look for Blocks if Medium/Hard
          // Check if this square allows opponent to win the local board
          // (We simulate placing Opponent piece here)
          const oppSim = simulateMove(localBoards, globalBoard, nextBoardIndex, humanPlayer, move.b, move.s);
          if (oppSim.globalBoard[move.b] === humanPlayer && globalBoard[move.b] === null) {
               score += 80; // Blocking a local win is high priority
          }

          // 4. Strategic Positioning (Hard)
          if (diff === "Hard") {
              const centerIdx = 4;
              const corners = [0,2,6,8];
              if (move.s === centerIdx) score += 5;
              if (corners.includes(move.s)) score += 3;

              // 5. Avoid sending opponent to a finished board (Giving free move)
              // The move.s determines the *next* board index.
              const targetBoardIdx = move.s;
              const targetBoardStatus = globalBoard[targetBoardIdx]; // Status BEFORE our move? No, status AFTER our move.
              // We need status after our move. The simulation `sim` has `nextBoardIndex`.
              
              if (sim.nextBoardIndex === null) {
                  // We gave them a free move (sent to won/full board) -> BAD
                  score -= 50; 
              } else {
                  // We sent them to specific board `sim.nextBoardIndex`.
                  // Is that board dangerous for us? (i.e. opponent can win it easily?)
                  // Too complex for shallow check.
                  // Just prefer constraining them.
                  score += 10;
              }
              
              // 6. Global Board Strategic Position (Winning center board is good)
             if (move.b === 4 && sim.globalBoard[4] === aiPlayer) score += 50;
          }

          if (score > bestScore) {
              bestScore = score;
              bestMove = move;
          }
      }

      return bestMove || shuffledMoves[0];

  }, []);


  const { isBotThinking } = useGameBot({
    gameMode,
    makeMove: (move) => move && executeMove(move.b, move.s),
    playerTurn: currentPlayer, 
    gameStarted,
    gameOver: !!winner,
    botStrategy: getBotMove,
    gameState: { localBoards, globalBoard, nextBoardIndex, currentPlayer },
    difficulty,
    botPlayerId: PLAYER_O 
  });


  // --- Rendering ---

  const isBoardActive = (boardIndex) => {
    if (winner) return false;
    return nextBoardIndex === null || nextBoardIndex === boardIndex;
  };

  const renderSquare = (boardIndex, squareIndex) => {
    const value = localBoards[boardIndex][squareIndex];
    const active = isBoardActive(boardIndex) && !globalBoard[boardIndex] && !winner && !value;
    // Disable if bot is thinking
    const botTurn = gameMode === 'PvC' && currentPlayer === PLAYER_O && !winner;
    const disabled = !!value || !!globalBoard[boardIndex] || !!winner || !active || botTurn;

    return (
      <button
        key={squareIndex}
        className={`
          w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base font-bold flex items-center justify-center
          border border-gray-200 transition-colors
          ${value === PLAYER_X ? "text-blue-600 bg-blue-50" : ""}
          ${value === PLAYER_O ? "text-red-600 bg-red-50" : ""}
          ${
            active && !botTurn
              ? "hover:bg-yellow-100 cursor-pointer"
              : "cursor-default"
          }
        `}
        onClick={() => executeMove(boardIndex, squareIndex)}
        disabled={disabled}
      >
        {value}
      </button>
    );
  };

  const renderLocalBoard = (boardIndex) => {
    const boardStatus = globalBoard[boardIndex];
    const isActive = isBoardActive(boardIndex);

    return (
      <div
        key={boardIndex}
        className={`
          relative p-1 sm:p-2 border-2 rounded-lg transition-all duration-300
          ${
            isActive && !boardStatus && !winner
              ? "border-yellow-400 shadow-[0_0_15px_rgba(250,204,21,0.5)] bg-yellow-50/20 scale-105 z-10"
              : "border-gray-300 bg-white"
          }
          ${boardStatus ? "opacity-90" : ""}
        `}
      >
        {/* Overlay for Won/Full Boards */}
        {boardStatus && (
          <div className="absolute inset-0 flex items-center justify-center z-20 bg-white/80 rounded-lg backdrop-blur-[1px]">
            <span
              className={`text-6xl sm:text-7xl font-bold ${
                boardStatus === PLAYER_X
                  ? "text-blue-600"
                  : boardStatus === PLAYER_O
                  ? "text-red-600"
                  : "text-gray-400"
              }`}
            >
              {boardStatus === "DRAW" ? "-" : boardStatus}
            </span>
          </div>
        )}

        {/* 3x3 Grid */}
        <div className="grid grid-cols-3 gap-0.5 sm:gap-1 bg-gray-100">
          {Array(9)
            .fill(null)
            .map((_, i) => renderSquare(boardIndex, i))}
        </div>
      </div>
    );
  };

  if (!gameStarted) {
      return (
          <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
              <div className="bg-white p-8 rounded-xl shadow-xl max-w-2xl w-full">
                <h1 className="text-4xl font-bold text-center text-blue-900 mb-8">Ultimate Tic-Tac-Toe</h1>
                
                <div className="mb-8 bg-blue-50 p-6 rounded-lg border border-blue-100">
                    <h3 className="flex items-center text-lg font-bold text-blue-800 mb-3">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        How to Play
                    </h3>
                    <ul className="list-disc pl-5 space-y-2 text-blue-900/80">
                        <li>The game is played on 9 small Tic-Tac-Toe boards.</li>
                        <li>Your move sends your opponent to a specific board.</li>
                        <li><strong>Example:</strong> If X plays in the top-right square of a small board, O must play in the top-right small board.</li>
                        <li>If sent to a completed board, you can play anywhere (Free Move!).</li>
                        <li>Win 3 small boards in a line to win the game.</li>
                    </ul>
                </div>

                <GameOptions
                    gameMode={gameMode}
                    setGameMode={setGameMode}
                    difficulty={difficulty}
                    setDifficulty={setDifficulty}
                    onStart={startGame}
                />
                <PlayerNameInput
                    player1Name={player1Name}
                    setPlayer1Name={setPlayer1Name}
                    player2Name={player2Name}
                    setPlayer2Name={setPlayer2Name}
                    gameMode={gameMode}
                />
              </div>
          </div>
      )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <button
            onClick={() => setGameStarted(false)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="font-medium hidden sm:inline">Back to Menu</span>
          </button>
          <h1 className="text-xl font-bold text-gray-800">Ultimate Tic-Tac-Toe</h1>
          <button
            onClick={() => setShowRules(!showRules)}
            className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
          >
            <HelpCircle className="h-6 w-6" />
          </button>
        </div>
      </div>

      <main className="flex-grow flex flex-col items-center justify-center p-4">
        {/* Rules Modal / Popover */}
        {showRules && (
          <div className="max-w-2xl w-full bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800 animate-fade-in">
             <h3 className="font-bold text-lg mb-2">Rules Reminder</h3>
             <ul className="list-disc pl-5">
                 <li>Win 3 local boards in a row on the global board.</li>
                 <li>If target board is full/won, play anywhere.</li>
             </ul>
          </div>
        )}

        {/* Game Status */}
        <div className="text-center mb-8 relative">
            {winner ? (
                <div className="animate-bounce-in">
                    <div className="inline-flex items-center justify-center p-4 bg-white rounded-full shadow-xl mb-4">
                        <Trophy className={`h-8 w-8 mr-3 ${winner === 'DRAW' ? 'text-gray-500' : 'text-yellow-500'}`} />
                        <span className="text-2xl font-bold text-gray-800">
                            {winner === "DRAW" ? "It's a Draw!" : `${getPlayerName(winner)} Wins!`}
                        </span>
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-center space-x-4 sm:space-x-8">
                     <div className={`
                        flex flex-col items-center p-3 rounded-lg transition-all duration-300 w-32 sm:w-40
                        ${currentPlayer === PLAYER_X ? "bg-white shadow-lg scale-105 border-b-4 border-blue-500" : "opacity-60"}
                     `}>
                        <div className="flex items-center mb-1">
                            <User className="w-4 h-4 mr-2" />
                            <span className="font-bold text-blue-600 truncate max-w-[6rem]">{getPlayerName(PLAYER_X)}</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-900">X</div>
                     </div>

                     <div className="text-xl text-gray-400 font-light flex flex-col items-center">
                         <span>VS</span>
                         {isBotThinking && <span className="text-xs text-purple-500 animate-pulse mt-1">Thinking...</span>}
                     </div>

                     <div className={`
                        flex flex-col items-center p-3 rounded-lg transition-all duration-300 w-32 sm:w-40
                        ${currentPlayer === PLAYER_O ? "bg-white shadow-lg scale-105 border-b-4 border-red-500" : "opacity-60"}
                     `}>
                        <div className="flex items-center mb-1">
                            {gameMode === 'PvC' ? <Bot className="w-4 h-4 mr-2" /> : <User className="w-4 h-4 mr-2" />}
                            <span className="font-bold text-red-600 truncate max-w-[6rem]">{getPlayerName(PLAYER_O)}</span>
                        </div>
                        <div className="text-2xl font-bold text-red-900">O</div>
                     </div>
                </div>
            )}
        </div>

        {/* The Board */}
        <div className="bg-gray-800 p-2 sm:p-4 rounded-xl shadow-2xl overflow-hidden relative">
            <div className="grid grid-cols-3 gap-2 sm:gap-4 bg-gray-800">
                {Array(9)
                    .fill(null)
                    .map((_, i) => renderLocalBoard(i))}
            </div>
            
            {/* Thinking Overlay */}
            {isBotThinking && !winner && (
                <div className="absolute inset-0 z-50 bg-black/10 flex items-center justify-center rounded-xl pointer-events-none">
                </div>
            )}
        </div>

        {/* Controls */}
        <div className="mt-8 flex gap-4">
             <button
                onClick={() => setGameStarted(false)}
                className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Menu
            </button>
            <button
                onClick={startGame}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-md"
            >
                <RefreshCw className="h-4 w-4 mr-2" />
                Restart
            </button>
        </div>
      </main>
    </div>
  );
};

export default UltimateTicTacToeGame;
